import { useAuth } from "@/auth.tsx";
import {
	checkAuthQueryOptions,
	useUpdateProfileMutation,
} from "@/services/auth-service.ts";
import { useQueryClient } from "@tanstack/react-query";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

export type Theme = "dark" | "light" | "system";

const isTheme = (value: unknown): value is Theme =>
	value === "dark" || value === "light" || value === "system";

interface ThemeProviderProps {
	children: React.ReactNode;
	defaultTheme?: Theme;
	storageKey?: string;
}

function getSystemTheme(): "dark" | "light" {
	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
}

function resolveTheme(theme: Theme): "dark" | "light" {
	return theme === "system" ? getSystemTheme() : theme;
}

const ThemeProviderContext = createContext<{
	theme: Theme;
	resolvedTheme: "dark" | "light";
	setTheme: (theme: Theme) => void;
}>({
	theme: "system",
	resolvedTheme: "light",
	setTheme: () => null,
});

export function ThemeProvider({
	children,
	defaultTheme = "system",
	storageKey = "ui-theme",
}: ThemeProviderProps) {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const { mutate: updateProfile } = useUpdateProfileMutation();

	// Seed from localStorage for fast first-paint before auth resolves.
	// Server-persisted preference takes over once `user` is loaded.
	const [theme, setThemeState] = useState<Theme>(() => {
		const stored = localStorage.getItem(storageKey);
		return isTheme(stored) ? stored : defaultTheme;
	});

	// Hydrate from the server-persisted preference when it becomes available.
	// localStorage is kept in sync as a cache for the next first-paint.
	useEffect(() => {
		if (user?.preferredTheme && isTheme(user.preferredTheme)) {
			setThemeState(user.preferredTheme);
			localStorage.setItem(storageKey, user.preferredTheme);
		}
	}, [user?.preferredTheme, storageKey]);
	const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">(() =>
		resolveTheme((localStorage.getItem(storageKey) as Theme) || defaultTheme),
	);

	useEffect(() => {
		const root = window.document.documentElement;
		const resolved = resolveTheme(theme);
		setResolvedTheme(resolved);

		if (!root.classList.contains(resolved)) {
			root.classList.remove("light", "dark");
			root.classList.add(resolved);
		}
	}, [theme]);

	// Listen for system theme changes
	useEffect(() => {
		if (theme !== "system") {
			return;
		}

		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		const handleChange = () => {
			const root = window.document.documentElement;
			const systemTheme = mediaQuery.matches ? "dark" : "light";
			root.classList.remove("light", "dark");
			root.classList.add(systemTheme);
			setResolvedTheme(systemTheme);
		};

		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, [theme]);

	// Memoized so consumers using `setTheme` in effect deps don't loop.
	// Without this, `useMutation`'s isPending toggle would re-create `setTheme`
	// every render, churning context consumers and re-firing their effects.
	const setTheme = useCallback(
		(next: Theme) => {
			localStorage.setItem(storageKey, next);
			setThemeState(next);
			// Persist server-side so the preference survives localStorage clears
			// (corporate browsers, privacy settings, device switches). Skip if no
			// authenticated session — the pre-login theme still works from
			// localStorage.
			if (user) {
				updateProfile(
					{ params: {}, body: { preferredTheme: next } },
					{
						onSuccess: () => {
							queryClient.invalidateQueries({
								queryKey: checkAuthQueryOptions().queryKey,
							});
						},
					},
				);
			}
		},
		[user, storageKey, updateProfile, queryClient],
	);

	const value = useMemo(
		() => ({ theme, resolvedTheme, setTheme }),
		[theme, resolvedTheme, setTheme],
	);

	return (
		<ThemeProviderContext.Provider value={value}>
			{children}
		</ThemeProviderContext.Provider>
	);
}

export const useTheme = () => {
	const context = useContext(ThemeProviderContext);
	if (context === undefined) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
};
